import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Copy, RefreshCw } from "lucide-react";

function Stat({ label, value, testid, accent = false }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-border-subtle bg-bg-surface p-5"
    >
      <p className="text-xs uppercase tracking-wider text-fg-secondary">{label}</p>
      <p className={`mt-2 text-3xl font-heading font-medium ${accent ? "text-accent-warm" : "text-fg-primary"}`}>
        {value}
      </p>
    </div>
  );
}

export default function Admin() {
  const [summary, setSummary] = useState(null);
  const [resets, setResets] = useState([]);
  const [usage, setUsage] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, r, u] = await Promise.all([
        api.get("/admin/summary"),
        api.get("/admin/pending-resets"),
        api.get("/admin/llm-usage", { params: { limit: 20 } }),
      ]);
      setSummary(s.data);
      setResets(r.data.pending || []);
      setUsage(u.data.records || []);
    } catch (err) {
      setError(extractApiError(err, "Failed to load admin data."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const copyLink = async (token) => {
    const url = `${window.location.origin}/reset-password?token=${encodeURIComponent(token)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Reset link copied to clipboard.");
    } catch {
      toast.error("Copy failed. Select and copy manually.");
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1
              data-testid="admin-page-title"
              className="text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary"
            >
              Admin
            </h1>
            <p className="mt-2 text-sm text-fg-secondary">
              Snapshot of user activity, deletions, LLM usage, and pending password resets.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/m5">
              <Button
                size="sm"
                data-testid="admin-open-m5-btn"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                Open M5 admin (overview, entitlements, finance, content, SAMPLE)
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAll}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mt-6" data-testid="admin-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {summary && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <Stat label="Users" value={summary.user_count} testid="stat-users" />
            <Stat label="Admins" value={summary.admin_count} testid="stat-admins" />
            <Stat label="Deletions" value={summary.deletion_log_count} testid="stat-deletions" />
            <Stat label="LLM calls" value={summary.llm_usage.total_calls} testid="stat-llm-calls" />
            <Stat
              label="Est. cost (USD)"
              value={`$${(summary.llm_usage.total_cost || 0).toFixed(4)}`}
              testid="stat-llm-cost"
              accent
            />
          </div>
        )}

        <section className="mt-10">
          <h2 className="text-xl font-heading font-medium text-fg-primary">Pending password resets</h2>
          <p className="mt-1 text-sm text-fg-secondary">
            Copy the link and share it out of band if the reset email did not reach the account owner, such as because of a spam filter or provider delay. Links expire 30 minutes after creation and can be used once.
          </p>
          <div className="mt-4 rounded-lg border border-border-subtle bg-bg-surface overflow-x-auto">
            <Table data-testid="pending-resets-table">
              <TableHeader>
                <TableRow className="border-border-subtle">
                  <TableHead className="text-fg-secondary">Email</TableHead>
                  <TableHead className="text-fg-secondary">Requested</TableHead>
                  <TableHead className="text-fg-secondary">Expires</TableHead>
                  <TableHead className="text-fg-secondary">Link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resets.length === 0 ? (
                  <TableRow className="border-border-subtle">
                    <TableCell colSpan={4} className="text-fg-secondary text-sm text-center py-8" data-testid="pending-resets-empty">
                      No pending resets.
                    </TableCell>
                  </TableRow>
                ) : resets.map((r) => (
                  <TableRow key={r.id} className="border-border-subtle" data-testid={`pending-reset-row-${r.email}`}>
                    <TableCell className="text-fg-primary">{r.email}</TableCell>
                    <TableCell className="text-fg-secondary text-xs">{new Date(r.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-fg-secondary text-xs">{new Date(r.expires_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyLink(r.plain_token)}
                        data-testid={`copy-reset-link-${r.email}`}
                        className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
                      >
                        <Copy className="h-3 w-3 mr-2" aria-hidden="true" />
                        Copy reset link
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-xl font-heading font-medium text-fg-primary">Recent LLM usage</h2>
          <p className="mt-1 text-sm text-fg-secondary">
            Raw prompts and narratives are never stored. Only provider, model, tokens, cost, latency, and success are logged.
          </p>
          <div className="mt-4 rounded-lg border border-border-subtle bg-bg-surface overflow-x-auto">
            <Table data-testid="llm-usage-table">
              <TableHeader>
                <TableRow className="border-border-subtle">
                  <TableHead className="text-fg-secondary">When</TableHead>
                  <TableHead className="text-fg-secondary">Provider</TableHead>
                  <TableHead className="text-fg-secondary">Model</TableHead>
                  <TableHead className="text-fg-secondary">Task</TableHead>
                  <TableHead className="text-fg-secondary text-right">In</TableHead>
                  <TableHead className="text-fg-secondary text-right">Out</TableHead>
                  <TableHead className="text-fg-secondary text-right">Cost</TableHead>
                  <TableHead className="text-fg-secondary text-right">Latency</TableHead>
                  <TableHead className="text-fg-secondary">OK</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usage.length === 0 ? (
                  <TableRow className="border-border-subtle">
                    <TableCell colSpan={9} className="text-fg-secondary text-sm text-center py-8" data-testid="llm-usage-empty">
                      No LLM usage yet.
                    </TableCell>
                  </TableRow>
                ) : usage.map((u) => (
                  <TableRow key={u.id} className="border-border-subtle" data-testid={`llm-usage-row-${u.id}`}>
                    <TableCell className="text-fg-secondary text-xs">{new Date(u.timestamp).toLocaleString()}</TableCell>
                    <TableCell className="text-fg-primary">{u.provider}</TableCell>
                    <TableCell className="text-fg-primary">{u.model}</TableCell>
                    <TableCell className="text-fg-secondary text-xs">{u.task_category}</TableCell>
                    <TableCell className="text-fg-primary text-right tabular-nums">{u.input_tokens}</TableCell>
                    <TableCell className="text-fg-primary text-right tabular-nums">{u.output_tokens}</TableCell>
                    <TableCell className="text-fg-primary text-right tabular-nums">${(u.estimated_cost || 0).toFixed(6)}</TableCell>
                    <TableCell className="text-fg-primary text-right tabular-nums">{u.latency_ms} ms</TableCell>
                    <TableCell>
                      <span className={u.success ? "text-progress-teal" : "text-destructive"}>
                        {u.success ? "yes" : "no"}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
